import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Separator } from '../ui/separator';
import { Switch } from '../ui/switch';
import { Settings, Loader2, Save, Euro } from 'lucide-react';
import { getTypeIcon } from './catalogShared';

function SpecField({ field, value, onChange }) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">
        {field.label}
        {field.unit && <span className="text-muted-foreground ml-1">({field.unit})</span>}
        {field.required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      {field.type === 'select' ? (
        <Select value={value} onValueChange={(v) => onChange(field.name, v)}>
          <SelectTrigger>
            <SelectValue placeholder="Seleziona..." />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map(opt => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : field.type === 'boolean' ? (
        <div className="flex items-center space-x-2">
          <Switch
            checked={value === true}
            onCheckedChange={(checked) => onChange(field.name, checked)}
          />
          <span className="text-sm text-muted-foreground">{value ? 'Sì' : 'No'}</span>
        </div>
      ) : (
        <Input
          type={field.type === 'number' ? 'number' : 'text'}
          value={value}
          onChange={(e) => onChange(field.name, field.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value)}
          placeholder={field.label}
        />
      )}
    </div>
  );
}

export function CatalogItemFormDialog({
  open, onOpenChange, editingItem,
  itemFormData, setItemFormData,
  types, selectedTypeSchema,
  saving, onSave
}) {
  const handleSpecChange = (fieldName, value) => {
    setItemFormData(prev => ({
      ...prev,
      technical_specs: {
        ...prev.technical_specs,
        [fieldName]: value
      }
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingItem ? 'Modifica Articolo' : 'Nuovo Articolo'}
          </DialogTitle>
          <DialogDescription>
            {editingItem ? 'Modifica i dati dell\'articolo' : 'Inserisci i dati del nuovo articolo nel catalogo'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Codice Articolo *</Label>
              <Input
                value={itemFormData.item_code}
                onChange={(e) => setItemFormData(prev => ({ ...prev, item_code: e.target.value.toUpperCase() }))}
                placeholder="MOD-JINKO-545"
                data-testid="item-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tipologia *</Label>
              <Select
                value={itemFormData.type_code}
                onValueChange={(v) => setItemFormData(prev => ({ ...prev, type_code: v, technical_specs: {} }))}
                disabled={!!editingItem}
              >
                <SelectTrigger data-testid="type-select">
                  <SelectValue placeholder="Seleziona tipologia" />
                </SelectTrigger>
                <SelectContent>
                  {types.filter(t => t.is_active).map(type => (
                    <SelectItem key={type.code} value={type.code}>
                      <span className="flex items-center gap-2">
                        {getTypeIcon(type.code)}
                        {type.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Marca *</Label>
              <Input
                value={itemFormData.brand}
                onChange={(e) => setItemFormData(prev => ({ ...prev, brand: e.target.value }))}
                placeholder="Jinko Solar"
                data-testid="brand-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Modello *</Label>
              <Input
                value={itemFormData.model}
                onChange={(e) => setItemFormData(prev => ({ ...prev, model: e.target.value }))}
                placeholder="Tiger Neo N-type 545W"
                data-testid="model-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrizione</Label>
            <Textarea
              value={itemFormData.description}
              onChange={(e) => setItemFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descrizione breve dell'articolo"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Euro className="h-4 w-4" />
                Prezzo Listino (€)
              </Label>
              <Input
                type="number"
                step="0.01"
                value={itemFormData.prezzo_listino}
                onChange={(e) => setItemFormData(prev => ({ ...prev, prezzo_listino: e.target.value }))}
                placeholder="0.00"
                data-testid="price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  checked={itemFormData.is_active}
                  onCheckedChange={(checked) => setItemFormData(prev => ({ ...prev, is_active: checked }))}
                  data-testid="active-switch"
                />
                <span className="text-sm">{itemFormData.is_active ? 'Attivo' : 'Inattivo'}</span>
              </div>
            </div>
          </div>

          {selectedTypeSchema.length > 0 && (
            <>
              <Separator />
              <div>
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <Settings className="h-4 w-4" />
                  Specifiche Tecniche
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {selectedTypeSchema.map(field => (
                    <SpecField
                      key={field.name}
                      field={field}
                      value={itemFormData.technical_specs?.[field.name] || ''}
                      onChange={handleSpecChange}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={itemFormData.notes}
              onChange={(e) => setItemFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Note aggiuntive..."
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annulla
          </Button>
          <Button onClick={onSave} disabled={saving} data-testid="save-item-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            {editingItem ? 'Aggiorna' : 'Crea'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
